import React, { FC, useState } from "react";
import { Modal } from "react-bootstrap";
import { useMutation, useQuery } from "@apollo/client";
import {
    LocalizationDataVariables,
    LocalizationQueryData,
    UpdateLocalizationVariables
} from "../../models/types";
import { GET_LOCALIZATION_DATA } from "../../graphql/queries";
import { useLocalState } from "../../graphql/hooks";
import { EditableJsonView } from "../editable/EditableJsonView";
import { UPDATE_LOCALIZATION } from "../../graphql/mutations";

interface Props {
    revisionId: number;
    revisionConfigId: number;
    localizationData: {
        localization: string;
        s3Key: string;
    };
    show: boolean;
    onClose: () => void;
}

export const LocalizationViewModal: FC<Props> = ({
    revisionId,
    revisionConfigId,
    localizationData,
    show,
    onClose
}) => {
    const { useDarkMode } = useLocalState();
    const [updating, setUpdating] = useState(false);
    const { data: { getLocalization } = {} } = useQuery<
        LocalizationQueryData,
        LocalizationDataVariables
    >(GET_LOCALIZATION_DATA, {
        variables: {
            revisionConfigId: revisionConfigId,
            localization: localizationData.s3Key
        },
        fetchPolicy: "network-only"
    });
    const [updateLocalizationMutation] =
        useMutation<UpdateLocalizationVariables>(UPDATE_LOCALIZATION);

    const saveLocalization = async (localizationStr: string) => {
        setUpdating(true);
        try {
            await updateLocalizationMutation({
                variables: {
                    revisionId: revisionId,
                    localizationKey: localizationData.s3Key,
                    localizationJson: JSON.stringify(localizationStr)
                },
                refetchQueries: [
                    {
                        query: GET_LOCALIZATION_DATA,
                        variables: {
                            revisionConfigId: revisionConfigId,
                            localization: localizationData.s3Key
                        }
                    }
                ]
            });
            setUpdating(false);
        } catch (error) {
            console.log("[DEBUG] update localization error: ", error);
        }
    };

    if (!(getLocalization && getLocalization.localizationJson)) {
        return null;
    }

    return (
        <Modal
            size="xl"
            show={show}
            fullscreen={true}
            onHide={onClose}
            backdrop="static"
            keyboard={false}
            className={useDarkMode ? "modal-dark" : ""}
        >
            <Modal.Header closeButton>
                {localizationData.localization}
            </Modal.Header>
            <Modal.Body className="p-0 m-0">
                <EditableJsonView
                    jsonData={JSON.stringify(
                        getLocalization.localizationJson,
                        null,
                        4
                    )}
                    updateJsonData={saveLocalization}
                    updating={updating}
                    inFullScreen={true}
                />
            </Modal.Body>
        </Modal>
    );
};
